import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectReferralCode,
  setReferralCode,
} from '../features/booking/bookingSlice';
import {
  clearReferral,
  lookupReferralCode,
  normalizeReferralCode,
  readReferral,
  REFERRAL_PARAM,
  saveReferral,
} from '../util/referralLink';

/**
 * Captures a friend's share link (`/book/location?ref=CODE`) into the booking
 * state and localStorage (30 days), and restores a stored one on a later
 * visit. Only when `enabled` (the Aperio preset); every other tenant is
 * untouched.
 *
 * The code is checked with GET /api/referral/:code: a code that is not valid,
 * or belongs to another tenant, is dropped. When the lookup cannot be made
 * (network) it is kept - the server checks it again at booking time anyway.
 * The `ref` param is left on the URL: usePrefilledAddress rewrites the query
 * string from its own snapshot, and a second writer would race it.
 */
export default function useReferralLink(
  enabled: boolean,
  tenantSubdomain?: string | null,
): string | null {
  const [params] = useSearchParams();
  const dispatch = useAppDispatch();
  const current = useAppSelector(selectReferralCode);
  const handled = useRef(false);

  useEffect(() => {
    if (!enabled || handled.current) return undefined;
    handled.current = true;

    const fromUrl = normalizeReferralCode(params.get(REFERRAL_PARAM));
    if (!fromUrl) {
      const stored = readReferral();
      if (stored && !current) dispatch(setReferralCode(stored));
      return undefined;
    }

    lookupReferralCode(fromUrl).then((result) => {
      const otherTenant = !!result?.tenant
        && !!tenantSubdomain
        && result.tenant.toLowerCase() !== tenantSubdomain.toLowerCase();
      if (result && (!result.valid || otherTenant)) {
        // Not a live code of this tenant: forget it (and any stale one).
        clearReferral();
        dispatch(setReferralCode(null));
        return;
      }
      saveReferral(fromUrl);
      dispatch(setReferralCode(fromUrl));
    });
    // The answer is written to the store even after this route unmounts
    // (the visitor may already be on the next step).
    return undefined;
  // Runs once per mount, on the params the visitor arrived with.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return enabled ? current : null;
}
